/**
 * State Transition Audit Log — spec §3.3
 * Firestore: projects/{projectId}/state_log/{logId}
 * Append-only. Every state transition is logged for retrospection.
 */

import {
  collection,
  addDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import type { StateMachineState, StateTransitionLog } from "@/types";

function stateLogRef(projectId: string) {
  return collection(db, "projects", projectId, "state_log");
}

export async function addStateLogEntry(
  projectId: string,
  fromState: StateMachineState,
  toState: StateMachineState,
  trigger: string,
  metadata?: Record<string, unknown>,
): Promise<string> {
  const docRef = await addDoc(stateLogRef(projectId), {
    from_state: fromState,
    to_state: toState,
    trigger,
    timestamp: serverTimestamp(),
    metadata: metadata ?? null,
  });
  return docRef.id;
}

export async function getStateLog(projectId: string): Promise<StateTransitionLog[]> {
  const q = query(stateLogRef(projectId), orderBy("timestamp", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as StateTransitionLog);
}

export function subscribeStateLog(
  projectId: string,
  onData: (entries: StateTransitionLog[]) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const q = query(stateLogRef(projectId), orderBy("timestamp", "desc"));
  return onSnapshot(
    q,
    (snap) => {
      onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as StateTransitionLog));
    },
    onError,
  );
}
